/**
 * W55: zoom and pan on a sheet, shared by the sheet viewer and the set review's full-page viewer. Pure helpers (no DOM
 * access) so both use the same maths: pinch / ctrl or ⌘ + wheel zoom at the cursor, a drag that pans once it has moved
 * more than a few pixels (less is still a click), and one analytics event per gesture.
 */

/** A drag shorter than this (px) is still a click: it places the Add to point or selects a marker */
export const DRAG_THRESHOLD_PX = 4;
/** A zoom gesture ends after this long without a wheel or pinch step */
export const GESTURE_IDLE_MS = 400;

export function clampZoom(zoom: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, zoom));
}

/** The zoom factor for one ctrl / ⌘ + wheel or trackpad-pinch step (deltaY < 0 zooms in) */
export function wheelZoomFactor(deltaY: number): number {
  return Math.exp(-deltaY * 0.01);
}

/**
 * A trackpad pinch arrives as ctrl + wheel with small, fractional deltas; a mouse wheel with ctrl / ⌘ held moves in
 * whole notches. Only for the analytics method.
 */
export function isPinchWheel(event: { ctrlKey: boolean; deltaY: number; deltaMode: number }): boolean {
  return event.ctrlKey && event.deltaMode === 0 && Math.abs(event.deltaY) < 50 && !Number.isInteger(event.deltaY);
}

/** The next step of a zoom ladder above (step 1) or below (step -1) the current zoom, which may sit between steps */
export function stepZoom(ladder: readonly number[], zoom: number, step: 1 | -1): number {
  const next = step > 0 ? ladder.find(z => z > zoom + 1e-6) : [...ladder].reverse().find(z => z < zoom - 1e-6);
  return next ?? (step > 0 ? ladder[ladder.length - 1] : ladder[0]);
}

/**
 * Keeps the point under the cursor fixed while the page grows or shrinks. `fraction` is where the cursor was on the
 * page (0-1 of its width / height) before the zoom; `pageStart` is the page's new left (or top) edge in viewport px and
 * `pageSize` its new width (or height). Returns how far to scroll so the same page point is under the cursor again.
 */
export function anchorScrollDelta(fraction: number, cursor: number, pageStart: number, pageSize: number): number {
  return pageStart + fraction * pageSize - cursor;
}

/** Where the cursor is on the page, as 0-1 of the page box (may fall outside 0-1 when the cursor is beside the page) */
export function pageFraction(cursor: number, pageStart: number, pageSize: number): number {
  return pageSize ? (cursor - pageStart) / pageSize : 0.5;
}

/**
 * One pointer's press-move-release: a pan once it has moved past DRAG_THRESHOLD_PX, a click otherwise. `move` returns
 * the scroll delta to apply (0 until it is a drag).
 */
export class DragTracker {
  private start: { x: number; y: number } | null = null;
  private last: { x: number; y: number } | null = null;
  /** The pointer moved far enough to be a pan */
  dragging = false;

  down(x: number, y: number): void {
    this.start = { x, y };
    this.last = { x, y };
    this.dragging = false;
  }

  move(x: number, y: number): { dx: number; dy: number } {
    if (!this.start || !this.last) {
      return { dx: 0, dy: 0 };
    }
    if (!this.dragging && Math.hypot(x - this.start.x, y - this.start.y) <= DRAG_THRESHOLD_PX) {
      return { dx: 0, dy: 0 };
    }
    this.dragging = true;
    const delta = { dx: this.last.x - x, dy: this.last.y - y };
    this.last = { x, y };
    return delta;
  }

  /** The button came up where we could not see it (outside the frame, capture lost): no drag, and no click follows */
  cancel(): void {
    this.start = null;
    this.last = null;
    this.dragging = false;
  }

  /** Released: whether it was a drag (the click that follows must not place a point or select a marker) */
  up(): boolean {
    const dragged = this.dragging;
    this.start = null;
    this.last = null;
    this.dragging = false;
    return dragged;
  }

  get active(): boolean {
    return !!this.start;
  }
}

/**
 * Review I2: a pan keeps the pointer once it has moved past the threshold, so the release still reaches the frame when
 * it happens outside it. Not on pointerdown: the click would then land on the frame instead of the page under it.
 * Synthetic pointers (specs, some pens) cannot be captured; the pan still works while over the frame.
 */
export function capturePointer(el: Element | null | undefined, pointerId: number): void {
  try {
    el?.setPointerCapture?.(pointerId);
  } catch {
    // no active pointer with that id
  }
}

export function releasePointer(el: Element | null | undefined, pointerId: number): void {
  try {
    if (el?.hasPointerCapture?.(pointerId)) {
      el.releasePointerCapture(pointerId);
    }
  } catch {
    // already released
  }
}

/** A move with no button down: the release happened somewhere we never heard about */
export function buttonsReleased(event: { buttons?: number; pointerType?: string }): boolean {
  return event.pointerType !== 'touch' && event.buttons === 0;
}

/**
 * Review M5: desktop Safari sends a trackpad pinch as gesturestart / gesturechange / gestureend (WebKit's GestureEvent,
 * `scale` relative to the start) rather than ctrl + wheel. Only WebKit fires them, so the listeners are inert elsewhere.
 */
export interface SafariGestureEvent extends UIEvent {
  scale: number;
  clientX: number;
  clientY: number;
}

export function isSafariGesture(event: Event): event is SafariGestureEvent {
  return typeof (event as Partial<SafariGestureEvent>).scale === 'number';
}

export class SafariPinch {
  private startZoom: number | null = null;

  /**
   * A Safari pinch is under way: a ctrl + wheel arriving with it is the same gesture, and on iPad the two-finger touch
   * pinch (pointer events) must not start beside it (review N2)
   */
  get active(): boolean {
    return this.startZoom !== null;
  }

  /** `touchPinching`: the pointer-event pinch already drives this pinch (iPad sends both); then the gesture only stops page zoom */
  start(event: Event, zoom: number, touchPinching = false): void {
    if (isSafariGesture(event)) {
      event.preventDefault();
      this.startZoom = touchPinching ? null : zoom;
    }
  }

  /** The zoom the pinch asks for, or null when it is not a pinch we started */
  change(event: Event): number | null {
    if (!isSafariGesture(event) || this.startZoom === null) {
      return null;
    }
    event.preventDefault();
    return this.startZoom * event.scale;
  }

  end(event: Event): void {
    if (isSafariGesture(event)) {
      event.preventDefault();
    }
    this.startZoom = null;
  }
}

/**
 * W61: the width a page of `naturalWidth` × `naturalHeight` takes to fit (contain) a box: as wide as the box, unless that
 * makes it taller than the box. Null when the page or the box has no size yet.
 */
export function containWidth(naturalWidth: number, naturalHeight: number, boxWidth: number, boxHeight: number): number | null {
  if (!(naturalWidth > 0) || !(naturalHeight > 0) || !(boxWidth > 0) || !(boxHeight > 0)) {
    return null;
  }
  return Math.min(boxWidth, boxHeight * (naturalWidth / naturalHeight));
}

/** Two touch points' distance, for a two-finger pinch */
export function pinchDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Collapses a gesture's many steps into one report, `idleMs` after the last step */
export class GestureReporter<T> {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: T | null = null;

  constructor(private readonly report: (value: T) => void, private readonly idleMs = GESTURE_IDLE_MS) {}

  step(value: T): void {
    this.pending = value;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => this.flush(), this.idleMs);
  }

  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.pending !== null) {
      const value = this.pending;
      this.pending = null;
      this.report(value);
    }
  }

  cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = null;
    this.pending = null;
  }
}

/** The wheel and Safari gesture handlers of a zoomable sheet area */
export interface SheetZoomListeners {
  wheel: (event: WheelEvent) => void;
  gesturestart: (event: Event) => void;
  gesturechange: (event: Event) => void;
  gestureend: (event: Event) => void;
}

/**
 * W75: binds a sheet area's wheel and Safari gesture listeners as non-passive, so the handlers may preventDefault: a
 * trackpad pinch (ctrl + wheel, or WebKit's gesture events) over the sheet zooms the sheet, never the page. Only on
 * `el`: outside the sheet the browser keeps its own behaviour. Returns the unbind.
 */
export function bindSheetZoomListeners(el: HTMLElement, handlers: SheetZoomListeners): () => void {
  const options: AddEventListenerOptions = { passive: false };
  const entries = Object.entries(handlers) as [keyof SheetZoomListeners, EventListener][];
  entries.forEach(([type, handler]) => el.addEventListener(type, handler, options));
  return () => entries.forEach(([type, handler]) => el.removeEventListener(type, handler, options));
}

/** What ScrollSheetZoom needs from its host */
export interface ScrollSheetZoomHost {
  /** The zoom steps for + / −; pinches stay between the first and last */
  ladder: readonly number[];
  zoom: () => number;
  /** Apply the zoom and render it now, so the page's new box can be measured */
  setZoom: (zoom: number) => void;
  /** The page element whose box grows with the zoom */
  page: () => HTMLElement | null;
  /** A drag pans (false: the drag is the host's, e.g. Draw area) */
  canPan: () => boolean;
}

/**
 * A sheet in a scrolling pane whose content grows with the zoom (the Place on map dialog), as the sheet picker (W75):
 * a trackpad pinch (ctrl / ⌘ + wheel, or Safari's gesture events) over the pane zooms the sheet at the cursor, never the
 * page; a plain wheel scrolls; a drag past DRAG_THRESHOLD_PX pans and the click ending it places nothing.
 */
export class ScrollSheetZoom {
  private readonly drag = new DragTracker();
  private readonly safariPinch = new SafariPinch();
  private pointer: number | null = null;
  private suppressClick = false;
  private el: HTMLElement | null = null;
  private unbind: (() => void) | null = null;

  constructor(private readonly host: ScrollSheetZoomHost) {}

  get panning(): boolean {
    return this.drag.dragging;
  }

  attach(el: HTMLElement | null): void {
    if (el === this.el) {
      return;
    }
    this.detach();
    this.el = el;
    if (el) {
      this.unbind = bindSheetZoomListeners(el, {
        wheel: e => this.onWheel(e),
        gesturestart: e => this.safariPinch.start(e, this.host.zoom()),
        gesturechange: e => {
          const zoom = this.safariPinch.change(e);
          if (zoom !== null && isSafariGesture(e)) {
            this.zoomAt(zoom, e.clientX, e.clientY);
          }
        },
        gestureend: e => this.safariPinch.end(e),
      });
    }
  }

  detach(): void {
    this.unbind?.();
    this.unbind = null;
    this.el = null;
    this.drag.cancel();
  }

  /** Zoom to `zoom` (within the ladder) keeping the page point under (clientX, clientY) where it is */
  zoomAt(zoom: number, clientX: number, clientY: number): void {
    const { ladder } = this.host;
    const next = clampZoom(zoom, ladder[0], ladder[ladder.length - 1]);
    if (Math.abs(next - this.host.zoom()) < 1e-3) {
      return;
    }
    const page = this.host.page();
    const before = page?.getBoundingClientRect();
    this.host.setZoom(next);
    if (!page || !this.el || !before?.width || !before.height) {
      return;
    }
    const after = page.getBoundingClientRect();
    this.el.scrollLeft += anchorScrollDelta(pageFraction(clientX, before.left, before.width), clientX, after.left, after.width);
    this.el.scrollTop += anchorScrollDelta(pageFraction(clientY, before.top, before.height), clientY, after.top, after.height);
  }

  /** + / −: the ladder's next step, about the middle of what is in view */
  zoomStep(step: 1 | -1): void {
    const next = stepZoom(this.host.ladder, this.host.zoom(), step);
    const box = this.el?.getBoundingClientRect();
    if (!box) {
      this.host.setZoom(next);
      return;
    }
    this.zoomAt(next, box.left + this.el.clientWidth / 2, box.top + this.el.clientHeight / 2);
  }

  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    event.preventDefault();
    if (this.safariPinch.active) {
      return;
    }
    this.zoomAt(this.host.zoom() * wheelZoomFactor(event.deltaY), event.clientX, event.clientY);
  }

  pointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch' || event.button !== 0 || !this.host.canPan()) {
      return;
    }
    this.drag.down(event.clientX, event.clientY);
    this.pointer = event.pointerId;
  }

  pointerMove(event: PointerEvent): void {
    if (!this.drag.active) {
      return;
    }
    if (buttonsReleased(event)) {
      this.drag.cancel();
      return;
    }
    const { dx, dy } = this.drag.move(event.clientX, event.clientY);
    if (this.drag.dragging && this.pointer !== null) {
      capturePointer(this.el, this.pointer);
    }
    if (this.el && (dx || dy)) {
      this.el.scrollLeft += dx;
      this.el.scrollTop += dy;
    }
  }

  pointerUp(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      return;
    }
    releasePointer(this.el, event.pointerId);
    this.pointer = null;
    if (this.drag.up()) {
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false));
    }
  }

  captureLost(): void {
    if (this.drag.active) {
      this.drag.cancel();
    }
  }

  /** Whether a click may place a point: false for the click that ends a pan */
  takeClick(): boolean {
    const allowed = !this.suppressClick;
    this.suppressClick = false;
    return allowed;
  }
}

/** The most pixels a sharp canvas may hold, and its longest side (browser canvas limits) */
export const SHARP_MAX_PIXELS = 16e6;
export const SHARP_MAX_SIDE = 16384;

/**
 * The part of a zoomed page to draw sharp: the page's box (client px) inside the scrolling pane's, as a rectangle in
 * page px, with the pixel ratio to draw it at (the screen's, less when the canvas would pass the browser's limits).
 * Null when none of the page is in view.
 */
export function sharpRegion(
  page: { left: number; top: number; width: number; height: number },
  pane: { left: number; top: number; width: number; height: number },
  devicePixelRatio: number,
  maxPixels = SHARP_MAX_PIXELS,
): { x: number; y: number; width: number; height: number; pixelRatio: number } | null {
  const left = Math.max(page.left, pane.left);
  const top = Math.max(page.top, pane.top);
  const right = Math.min(page.left + page.width, pane.left + pane.width);
  const bottom = Math.min(page.top + page.height, pane.top + pane.height);
  if (right - left < 1 || bottom - top < 1) {
    return null;
  }
  const width = Math.round(right - left);
  const height = Math.round(bottom - top);
  const ratio = Math.min(devicePixelRatio || 1, Math.sqrt(maxPixels / (width * height)), SHARP_MAX_SIDE / Math.max(width, height));
  return { x: Math.round(left - page.left), y: Math.round(top - page.top), width, height, pixelRatio: ratio };
}
